// ---------------------------------------------------------------------------
// AWBM model (Australian Water Balance Model), simplified 4-parameter
// version — Boughton (2004), "The Australian water balance model",
// Environmental Modelling & Software 19.
//
// Formulation confirmed by decompiling EvalHid's real calculation engine
// (Evalhid.dll, .NET 6, via ilspycmd — see class Awbm.calculo_awbm()), not
// against an external source like was done before (hydromad): the .exe
// splits precipitation and ETP BY AREA before they enter each of the 3
// reservoirs (each reservoir i receives `a_i·P` and `a_i·ETP`, not the whole
// P/ETP), and the 3 reservoirs' capacities are Cave multiplied by three
// fixed constants from the app itself (0.075/0.762/1.524) — not hydromad's
// splitting rule used in the previous implementation. The areas
// (0.134/0.433/0.433) do match the ones already in use.
//
// Each reservoir's initial level is Have·a_i bounded to its own capacity —
// not Have's absolute value bounded directly.
//
// Note on Ksurf/Kb: `baseTemplate.ts` describes them as "discharge
// coefficient" (unit "1/time", like Alfa in Témez or K0-K2 in HBV, where a
// small value implies a slow recession) — but the real formula uses them as
// a RETAINED FRACTION (discharge = reservoir·(1−K), retained =
// reservoir·K): with the default value Kb=0.02, this discharges 98% of the
// base flow reservoir every step, exactly the opposite of a slow, sustained
// base flow. It's the same kind of mismatch between the parameter's label
// and its real use already found in HBV's K0/K1/K2.
//
// The base flow reservoir is ALWAYS calculated, whether or not the sub-basin
// has a real aquifer assigned — but `contribution` only includes it when
// `hasAquifer` is false. Confirmed in the real source (2026-08, see
// CLAUDE.md's engine-source audit): `Awbm.cs` checks `cod_acuifero == "0"`
// and, with a real aquifer assigned, excludes base flow from the
// sub-basin's own contribution (assumed to flow to the aquifer's own
// aggregate balance instead — see aggregation.ts).
//
// Pumping (`bomb`, mm/step): subtracted from the base flow reservoir's own
// inflow (`Awbm.cs`: `hsubt = Max(0.000001, hsubt - esc_subt + perc -
// bomb)`, floored at the same small positive value as the reservoir itself
// — see below). Resolved from the sub-basin's own `Ent_Bomb.csv` column and
// converted to mm/step before reaching here — see temez.ts's own header
// for the full resolution/conversion chain, shared by all 6 models.
// `null` only when the sub-basin has no pumping column of its own, or
// "Modelar bombeos" is unchecked.
// ---------------------------------------------------------------------------

export interface AwbmParameters {
  /** Cave (mm): average soil capacity, scaled by the fixed 3-reservoir split constants. */
  cave: number;
  /** Bfi (dimensionless, 0-1): fraction of the surplus that goes to base flow instead of surface runoff. */
  bfi: number;
  /** Ksurf (dimensionless, 0-1): fraction retained (NOT released) from the surface routing reservoir each step. */
  ksurf: number;
  /** Kb (dimensionless, 0-1): fraction retained (NOT released) from the base flow routing reservoir each step. */
  kb: number;
}

interface AwbmState {
  /** Have (mm): initial average height, split by area across the 3 surface reservoirs. */
  have: number;
  /** Vsup (mm): initial surface routing reservoir storage. */
  vsup: number;
  /** Vacu (mm): initial base flow routing reservoir storage. */
  vacu: number;
}

interface AwbmDetail {
  [key: string]: Float64Array;
  /** H1 (mm): surface reservoir 1 after the step. */
  reservoir1: Float64Array;
  /** H2 (mm): surface reservoir 2 after the step. */
  reservoir2: Float64Array;
  /** H3 (mm): surface reservoir 3 after the step. */
  reservoir3: Float64Array;
  /** Total actual evapotranspiration (sum of the 3 reservoirs). */
  etr: Float64Array;
  /** Total surplus from the 3 reservoirs, before splitting into surface/base. */
  surplus: Float64Array;
  /** Percolation into the base flow reservoir. */
  percolation: Float64Array;
  /** Surface routing reservoir after the step. */
  surfaceReservoir: Float64Array;
  /** Base flow routing reservoir after the step. */
  baseReservoir: Float64Array;
  /** Surface runoff (mm). */
  surfaceRunoff: Float64Array;
  /** Base flow (mm). */
  baseFlow: Float64Array;
}

interface AwbmResult {
  /** Contribution (surface runoff + base flow) per time step, mm. */
  contribution: Float64Array;
  /** Step-by-step internal detail series (see Sal_parciales.csv in the real .exe). */
  detail: AwbmDetail;
}

/** Area fractions of the 3 surface reservoirs (fixed in the real app). */
const AREAS = [0.134, 0.433, 0.433];
/** Fixed multipliers of Cave for each reservoir's capacity (fixed in the real app). */
const CAPACITY_MULTIPLIERS = [0.075, 0.762, 1.524];

/**
 * Simulates the AWBM model (4-parameter version) for a single sub-basin.
 * `precipitation` and `etp` must already come adjusted (precipAdjustment/etpAdjustment
 * applied) and aligned step by step.
 */
export function calculateAwbm(
  precipitation: Float64Array,
  etp: Float64Array,
  parameters: AwbmParameters,
  initialState: AwbmState,
  hasAquifer: boolean,
  pumping: Float64Array | null
): AwbmResult {
  const { cave, bfi, ksurf, kb } = parameters;
  const n = precipitation.length;
  const contribution = new Float64Array(n);
  const detail: AwbmDetail = {
    reservoir1: new Float64Array(n),
    reservoir2: new Float64Array(n),
    reservoir3: new Float64Array(n),
    etr: new Float64Array(n),
    surplus: new Float64Array(n),
    percolation: new Float64Array(n),
    surfaceReservoir: new Float64Array(n),
    baseReservoir: new Float64Array(n),
    surfaceRunoff: new Float64Array(n),
    baseFlow: new Float64Array(n),
  };

  const capacities = CAPACITY_MULTIPLIERS.map((m) => m * cave);
  const s = capacities.map((c, i) => Math.min(c, initialState.have * AREAS[i]));

  let surfaceStorage = initialState.vsup;
  let baseStorage = initialState.vacu;

  for (let t = 0; t < n; t++) {
    const p = Number.isFinite(precipitation[t]) ? precipitation[t] : 0;
    const e = Number.isFinite(etp[t]) ? etp[t] : 0;

    let totalSurplus = 0;
    let totalEtr = 0;
    for (let i = 0; i < AREAS.length; i++) {
      const input = AREAS[i] * p;
      const actualEtr = Math.min(s[i] + input, AREAS[i] * e);
      const surplus = Math.max(0, s[i] + input - actualEtr - capacities[i]);
      s[i] = s[i] + input - actualEtr - surplus;
      totalSurplus += surplus;
      totalEtr += actualEtr;
    }

    const percolation = totalSurplus * bfi;
    const baseFlow = baseStorage * (1 - kb);
    const bomb = pumping && Number.isFinite(pumping[t]) ? pumping[t] : 0;
    // Floored at a small positive value (not 0), matching the real source
    // exactly (Awbm.cs) — confirmed 2026-08, see CLAUDE.md's engine-source
    // audit. Without it, an extreme pumping withdrawal could drive the
    // reservoir negative.
    baseStorage = Math.max(0.000001, baseStorage - baseFlow + percolation - bomb);

    const surfaceRunoff = surfaceStorage * (1 - ksurf);
    surfaceStorage = Math.max(0.000001, surfaceStorage - surfaceRunoff + totalSurplus * (1 - bfi));

    contribution[t] = hasAquifer ? surfaceRunoff : surfaceRunoff + baseFlow;

    detail.reservoir1[t] = s[0];
    detail.reservoir2[t] = s[1];
    detail.reservoir3[t] = s[2];
    detail.etr[t] = totalEtr;
    detail.surplus[t] = totalSurplus;
    detail.percolation[t] = percolation;
    detail.surfaceReservoir[t] = surfaceStorage;
    detail.baseReservoir[t] = baseStorage;
    detail.surfaceRunoff[t] = surfaceRunoff;
    detail.baseFlow[t] = baseFlow;
  }

  return { contribution, detail };
}
